import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Shield, LogOut, User as UserIcon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { logger } from "@/lib/logger";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  const dashboardPath =
    user?.role === "admin"
      ? "/admin"
      : user?.role === "contractor"
      ? "/contractor"
      : "/dashboard";

  useEffect(() => {
    if (!user) {
      setUnread(0);
      return;
    }
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get("/me/unread-total");
        if (!cancelled) setUnread(r.data?.unread || 0);
      } catch (err) {
        // Polling counter — transient errors should not surface to the user.
        // Log so ops sees recurring failures.
        logger.debug("[navbar] unread-total poll failed:", err?.response?.status);
      }
    };
    tick();
    const i = setInterval(tick, 15000);
    return () => {
      cancelled = true;
      clearInterval(i);
    };
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <nav
      className="glass-nav fixed top-0 inset-x-0 z-50"
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-2 group"
          data-testid="brand-logo-link"
        >
          <div className="w-9 h-9 rounded-lg bg-[#1E3F33] flex items-center justify-center">
            <Shield className="w-5 h-5 text-[#F9F8F6]" />
          </div>
          <span className="font-['Outfit'] text-xl font-semibold tracking-tight">
            ClaimScope
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8 text-sm text-gray-600">
          <Link to="/#how" className="hover:text-[#1E3F33]" data-testid="nav-how-link">
            How it works
          </Link>
          <Link to="/#features" className="hover:text-[#1E3F33]" data-testid="nav-features-link">
            Features
          </Link>
          <Link to="/#contractors" className="hover:text-[#1E3F33]" data-testid="nav-contractors-link">
            Contractors
          </Link>
        </div>

        <div className="flex items-center gap-3">
          {user ? (
            <>
              <Link
                to={dashboardPath}
                className="hidden sm:flex items-center gap-2 text-sm text-gray-700 hover:text-[#1E3F33] relative"
                data-testid="nav-dashboard-link"
              >
                <UserIcon className="w-4 h-4" />
                {user.full_name?.split(" ")[0] || "Dashboard"}
                {unread > 0 && (
                  <span
                    className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-[#C25934] text-[#F9F8F6] text-[10px] font-semibold leading-none"
                    data-testid="nav-unread-badge"
                    title={`${unread} unread message${unread === 1 ? "" : "s"}`}
                  >
                    {unread > 99 ? "99+" : unread}
                  </span>
                )}
              </Link>
              <Button
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-1" /> Sign out
              </Button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="text-sm text-gray-700 hover:text-[#1E3F33]"
                data-testid="nav-login-link"
              >
                Sign in
              </Link>
              <Link to="/signup" data-testid="nav-signup-link">
                <Button className="btn-primary rounded-full px-5">
                  Get started
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
